import {Component} from '@packages/systems/wfdl';
import {EList} from '@packages/systems/wfdl-ast';
import {getSlotDefaultLists} from '@packages/utilities/platform-slots';
import type {
  ComponentDependencies,
  Ix2Type,
  Ix3Type,
  SymbolsLocalizationData,
} from '../types';
import type {StyleBlockMapType} from '@packages/utilities/platform';
import {
  AnyVariableType,
  VariableID,
} from '@packages/systems/core/utils/StyleVariables';
import {SwatchType} from '@packages/systems/core/utils/StyleTypes';
import {
  getAllStyleBlockIdsInExpression,
  getAllStyleBlocksToInclude,
  getTagStyleBlockIdsWithStyleVariants,
} from '@packages/utilities/platform';
import {getInteractionsFromComponentRender} from './interactions';
import {getAllAssetsToInclude} from './assets';
import {migrateStyleBlocksToVariables} from './migrateStyleBlocks';
import {getAllSwatchesToInclude} from './getSwatches';
import {getAllStyleVariablesAsPluginReferences} from './getStyleVariables';
import {getAllSymbolsLocalizationsToInclude} from './localization';
import {getIx3FromComponentScope, toNonEmptyIx3} from './ix3Interactions';

export const isServer = typeof process !== 'undefined';

export function asyncify<T extends (...args: Parameters<T>) => ReturnType<T>>(
  fn: T
) {
  return async function (...args: Parameters<T>): Promise<ReturnType<T>> {
    await new Promise((resolve) =>
      isServer ? setImmediate(resolve) : setTimeout(resolve, 0)
    );
    const result = fn(...args);
    return new Promise((resolve) => resolve(result));
  };
}

/**
 * Given a Component, collects all its dependencies:
 * - style blocks
 * - style variable ids
 * - interactions
 * - assets
 * - swatches (legacy)
 * - symbols localizations data
 */
export const collectComponentDependenciesSync = (
  libraryId: string | undefined,
  componentId: Component.ComponentName,
  component: Component.ComponentType,
  styleBlockMap: StyleBlockMapType,
  ix2: Ix2Type,
  ix3: Ix3Type | undefined,
  swatchMap: Map<string, SwatchType>,
  variableMap: Map<VariableID, AnyVariableType>,
  componentMap: Component.ComponentMap,
  symbolsLocalizations?: SymbolsLocalizationData
): ComponentDependencies => {
  const render = Component.getRender(component);
  const dataType = Component.getDataType(component);
  const variants = Component.getVariants(component);
  const slotDefaultLists = getSlotDefaultLists(component).map(
    ([, list]) => list
  );
  const content =
    slotDefaultLists.length > 0 ? EList([render, ...slotDefaultLists]) : render;
  const styleIdsOnElementPreset =
    getAllStyleBlockIdsInExpression(styleBlockMap)(content);
  const tagStyleIdsWithVariants = getTagStyleBlockIdsWithStyleVariants(
    variants,
    styleBlockMap
  );
  const interactions = getInteractionsFromComponentRender(
    content,
    ix2,
    styleIdsOnElementPreset
  );
  const ix3ForComponent = toNonEmptyIx3(
    ix3 ? getIx3FromComponentScope(ix3, componentId) : undefined
  );

  const styleBlocks = getAllStyleBlocksToInclude(
    [...styleIdsOnElementPreset, ...tagStyleIdsWithVariants],
    styleBlockMap
  );
  const symbolsLocalizationsToInclude = symbolsLocalizations
    ? getAllSymbolsLocalizationsToInclude(render, symbolsLocalizations)
    : undefined;

  const assetsIds = getAllAssetsToInclude(
    content,
    dataType,
    styleBlocks,
    componentMap,
    symbolsLocalizationsToInclude,
    libraryId
  );

  const {migratedStyleBlocks, variablesIds, swatchIds} =
    migrateStyleBlocksToVariables(styleBlocks, variableMap);

  const swatches = getAllSwatchesToInclude(swatchIds, swatchMap);

  return {
    interactions,
    ...(ix3ForComponent && {ix3: ix3ForComponent}),
    assetsIds,
    styleBlocks: migratedStyleBlocks,
    swatches,
    styleVariableIds: getAllStyleVariablesAsPluginReferences(
      variablesIds,
      libraryId
    ),
    ...(symbolsLocalizationsToInclude && {
      symbolsLocalizations: symbolsLocalizationsToInclude,
    }),
  };
};

export const collectComponentDependencies = asyncify(
  collectComponentDependenciesSync
);
